import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaMicrophone, FaMusic } from 'react-icons/fa';
import { DataContext } from '../DataContexts/DataContext';
import ArtistEdit from './ArtistEdit';
import AlbumEdit from './AlbumEdit';
import SongEdit from './SongEdit';
import PropertyChecks, { trackMatchesProperties, DELETED_KEY } from './BrowseProperties';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';
import DashboardSection from '../components/DashboardSection';
import '../PlaylistManager/PlaylistAssign.css';
import './BrowsePage.css';

// Library > Browse (DJB-91): the old "Artist Search" and "Songs" tabs merged
// into one sub-page in the ps-* Curator look. A single search box matches
// artist names, track names and genres; the left column mixes artist cards
// (darker blue with a 🎤 icon and a track-count pill, DJB-102) and track
// cards (🎵 "track – artist", genre on the right).
//
// DJB-92 filter area: release-year and tag filters plus on/off property
// checkmarks (Never Idle / On Demand / Never New / Mix / VIP; default
// "All songs") — the old genre/subgenre filters are gone. The filters apply
// to tracks; an artist card stays visible while it has at least one matching
// track (its pill shows the matching count). One pill-shaped Clear button
// resets everything, filling red only when filters are active or hovered.
const PAGE_SIZE = 30;

const normalize = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const BrowsePage = () => {
  const { artists, tags, loading, error } = useContext(DataContext);
  const [searchParams] = useSearchParams();
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedSong, setSelectedSong] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [releaseYear, setReleaseYear] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [activeProps, setActiveProps] = useState([]);
  const [page, setPage] = useState(1);
  const [browserOpen, setBrowserOpen] = useState(true);

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open the section even if the user collapsed it
  useDeepLinkedSection({ 'section-library-browser': 'browser' }, () =>
    setBrowserOpen(true)
  );

  // Deep links (?artistId= / ?songId=) pre-select their target once the
  // library is loaded; the ref keeps a data refresh from stomping whatever
  // the user selected afterwards.
  const artistIdParam = searchParams.get('artistId');
  const songIdParam = searchParams.get('songId');
  const appliedDeepLink = useRef(null);

  useEffect(() => {
    const key = `${artistIdParam}|${songIdParam}`;
    if (appliedDeepLink.current === key) return;
    if (!artists.length || (!artistIdParam && !songIdParam)) return;

    const artist = artistIdParam
      ? artists.find((a) => a.id === artistIdParam)
      : artists.find((a) =>
          a.albums.some((album) => album.tracks.some((t) => t.id === songIdParam))
        );
    if (!artist) return;

    let song = null;
    if (songIdParam) {
      for (const album of artist.albums) {
        song = album.tracks.find((t) => t.id === songIdParam) || null;
        if (song) break;
      }
    }

    setSelectedArtist(artist);
    setSelectedAlbum(null);
    setSelectedSong(song);
    appliedDeepLink.current = key;
  }, [artists, artistIdParam, songIdParam]);

  // Keep the selections pointing at the freshest objects when the library
  // refreshes after an edit
  useEffect(() => {
    if (!selectedArtist) return;
    const updatedArtist = artists.find((a) => a.id === selectedArtist.id);
    if (!updatedArtist) return;
    if (updatedArtist !== selectedArtist) setSelectedArtist(updatedArtist);
    if (selectedAlbum) {
      const updatedAlbum = updatedArtist.albums.find((al) => al.id === selectedAlbum.id);
      if (updatedAlbum && updatedAlbum !== selectedAlbum) setSelectedAlbum(updatedAlbum);
    }
    if (selectedSong) {
      for (const album of updatedArtist.albums) {
        const updatedSong = album.tracks.find((t) => t.id === selectedSong.id);
        if (updatedSong) {
          if (updatedSong !== selectedSong) setSelectedSong(updatedSong);
          break;
        }
      }
    }
  }, [artists, selectedArtist, selectedAlbum, selectedSong]);

  // One query over the whole library: artists match on their name, tracks on
  // their title or genre. Deleted tracks (and artists left without any live
  // track) stay hidden unless the DJB-173 Deleted checkmark is on, which
  // flips the list to deleted tracks only. The DJB-92 filters (year, tags,
  // properties) apply to tracks; an artist card survives while at least one
  // of its tracks passes, and its pill counts only the passing tracks.
  const filtersActive =
    releaseYear !== '' || selectedTags.length > 0 || activeProps.length > 0;

  const results = useMemo(() => {
    const query = normalize(searchQuery.trim());
    const yearFilter = releaseYear === '' ? null : parseInt(releaseYear, 10);
    // With Deleted active the deleted-only narrowing happens inside
    // trackMatchesProperties (track.deleted is just another boolean flag)
    const includeDeleted = activeProps.includes(DELETED_KEY);
    const trackPassesFilters = (track) =>
      (yearFilter === null || track.releaseyear === yearFilter) &&
      selectedTags.every((tag) => track.tags && track.tags.includes(tag.id)) &&
      trackMatchesProperties(track, activeProps);

    const artistCards = [];
    const trackCards = [];
    // A collab track lives under every participating artist with the same
    // track id, and gets a card under each of them (DJB-174). The per-artist
    // guard keeps a track that recurs across one artist's albums to a single
    // card, so the artist-scoped React keys stay unique and no stale cards
    // survive a search change or clear (DJB-104).
    const seenArtistTrackIds = new Set();

    artists.forEach((artist) => {
      const matchingTracks = [];
      artist.albums.forEach((album) => {
        album.tracks.forEach((track) => {
          if ((includeDeleted || !track.deleted) && trackPassesFilters(track)) {
            matchingTracks.push({ track, album });
          }
        });
      });
      if (matchingTracks.length === 0) return;

      if (!query || normalize(artist.name).includes(query)) {
        artistCards.push({ type: 'artist', artist, trackCount: matchingTracks.length });
      }
      matchingTracks.forEach(({ track, album }) => {
        const seenKey = `${artist.id}|${track.id}`;
        if (seenArtistTrackIds.has(seenKey)) return;
        if (!query || normalize(track.title).includes(query) || normalize(track.genre).includes(query)) {
          seenArtistTrackIds.add(seenKey);
          trackCards.push({ type: 'track', track, album, artist });
        }
      });
    });

    artistCards.sort((a, b) => a.artist.name.localeCompare(b.artist.name));
    trackCards.sort(
      (a, b) =>
        a.track.title.localeCompare(b.track.title) ||
        a.artist.name.localeCompare(b.artist.name)
    );
    return { artistCards, trackCards };
  }, [artists, searchQuery, releaseYear, selectedTags, activeProps]);

  const mixedList = useMemo(
    () => [...results.artistCards, ...results.trackCards],
    [results]
  );
  const visibleItems = mixedList.slice(0, PAGE_SIZE * page);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, releaseYear, selectedTags, activeProps]);

  const clearFilters = () => {
    setReleaseYear('');
    setSelectedTags([]);
    setTagSearch('');
    setActiveProps([]);
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
    if (bottom && visibleItems.length < mixedList.length) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSelectArtist = (artist) => {
    setSelectedArtist(artist);
    setSelectedAlbum(null);
    setSelectedSong(null);
  };

  const handleSelectTrack = (item) => {
    setSelectedArtist(item.artist);
    setSelectedAlbum(null);
    setSelectedSong(item.track);
  };

  const handleSongSelection = (song) => {
    setSelectedSong(song);
    setSelectedAlbum(null);
  };

  const handleAlbumSelection = (album) => {
    setSelectedAlbum(album);
    setSelectedSong(null);
  };

  if (loading) {
    return (
      <div className="ps-page ps-page--library-browse">
        <p className="ps-status">Loading data, please wait... 🎵</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ps-page ps-page--library-browse">
        <p className="ps-status ps-status--error">Error loading data: {error}</p>
      </div>
    );
  }

  return (
    <div className="ps-page ps-page--library-browse">
      <DashboardSection
        id="section-library-browser"
        title="Library Browser"
        info="Doorzoek de bibliotheek op artiest, nummer of genre."
        isOpen={browserOpen}
        onToggle={() => setBrowserOpen((open) => !open)}
        bodyId="ps-body-library-browse"
      >
          {browserOpen && (
            <div className="ps-columns">
              <div className="ps-column ps-column--browse">
                <div className="ps-filters browse-filters">
                  <input
                    type="text"
                    className="ps-input"
                    placeholder="Search artists, tracks or genres..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="browse-filter-row">
                    <input
                      type="number"
                      className="ps-input browse-year-input"
                      placeholder="Release year"
                      aria-label="Filter by release year"
                      value={releaseYear}
                      onChange={(e) => setReleaseYear(e.target.value)}
                    />
                    <div className="browse-tag-dropdown">
                      <input
                        type="text"
                        className="ps-input"
                        placeholder="Filter by tag..."
                        aria-label="Filter by tag"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        onFocus={() => setShowTagDropdown(true)}
                        onBlur={() => setShowTagDropdown(false)}
                      />
                      {showTagDropdown && (
                        <div className="browse-dropdown-list">
                          {(tags || [])
                            .filter(
                              (tag) =>
                                normalize(tag.name).includes(normalize(tagSearch)) &&
                                !selectedTags.some((t) => t.id === tag.id)
                            )
                            .map((tag) => (
                              <div
                                key={tag.id}
                                className="browse-dropdown-item"
                                // onMouseDown so the pick lands before the input blurs
                                onMouseDown={() => {
                                  setSelectedTags((prev) => [...prev, tag]);
                                  setTagSearch('');
                                }}
                              >
                                {tag.name}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedTags.length > 0 && (
                    <div className="ps-tag-list browse-selected-tags">
                      {selectedTags.map((tag) => (
                        <span key={tag.id} className="ps-tag-chip browse-chip">
                          {tag.name}
                          <button
                            type="button"
                            className="browse-chip-remove"
                            aria-label={`Remove tag filter ${tag.name}`}
                            onClick={() =>
                              setSelectedTags((prev) => prev.filter((t) => t.id !== tag.id))
                            }
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <PropertyChecks active={activeProps} onChange={setActiveProps} />
                  <div className="browse-filter-foot">
                    <button
                      type="button"
                      className={`browse-clear-pill ${filtersActive ? 'is-active' : ''}`}
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  </div>
                </div>
                <div className="ps-column-head">
                  <h3 className="ps-column-label">Results</h3>
                  <span className="ps-count-badge">{results.artistCards.length} artists</span>
                  <span className="ps-count-badge">{results.trackCards.length} tracks</span>
                </div>
                <div className="ps-list-scroll" onScroll={handleScroll}>
                  {visibleItems.length === 0 ? (
                    <p className="ps-empty">No artists or tracks match this search.</p>
                  ) : (
                    <ul className="browse-list">
                      {visibleItems.map((item) =>
                        item.type === 'artist' ? (
                          <li
                            key={`artist-${item.artist.id}`}
                            className={`browse-card browse-card--artist ${
                              !selectedSong && !selectedAlbum && selectedArtist?.id === item.artist.id
                                ? 'selected'
                                : ''
                            }`}
                            onClick={() => handleSelectArtist(item.artist)}
                          >
                            <FaMicrophone className="browse-card-icon" aria-hidden="true" />
                            <span className="browse-card-label">{item.artist.name}</span>
                            <span className="ps-subs-badge browse-track-pill">
                              {item.trackCount} tracks
                            </span>
                          </li>
                        ) : (
                          <li
                            // A collab track renders once per artist (DJB-174),
                            // so the key needs the artist to stay unique
                            key={`track-${item.artist.id}-${item.track.id}`}
                            className={`browse-card browse-card--track ${
                              selectedSong?.id === item.track.id &&
                              selectedArtist?.id === item.artist.id
                                ? 'selected'
                                : ''
                            } ${item.track.deleted ? 'browse-card--deleted' : ''}`}
                            onClick={() => handleSelectTrack(item)}
                          >
                            <FaMusic className="browse-card-icon" aria-hidden="true" />
                            <span className="browse-card-label">
                              {item.track.title}
                              <span className="browse-track-artist"> – {item.artist.name}</span>
                            </span>
                            {item.track.genre && (
                              <span className="browse-genre">{item.track.genre}</span>
                            )}
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </div>
              </div>
              <div className="ps-column ps-column--browse-editor">
                <div className="ps-column-head">
                  <h3 className="ps-column-label">Artist Editor</h3>
                </div>
                <div className="browse-editor-scroll">
                  <ArtistEdit
                    artist={selectedArtist}
                    onAlbumSelect={handleAlbumSelection}
                    onSongSelect={handleSongSelection}
                    selectedSong={selectedSong}
                    selectedAlbum={selectedAlbum}
                    deleted={false}
                  />
                </div>
              </div>
              <div className="ps-column ps-column--browse-detail">
                <div className="ps-column-head">
                  <h3 className="ps-column-label">Track Details</h3>
                </div>
                <div className="browse-editor-scroll">
                  {selectedAlbum ? (
                    <AlbumEdit
                      selectedArtistId={selectedArtist.id}
                      selectedAlbum={selectedAlbum}
                    />
                  ) : selectedSong ? (
                    <SongEdit
                      allArtists={artists}
                      selectedArtist={selectedArtist}
                      selectedSong={selectedSong}
                    />
                  ) : (
                    <p className="ps-placeholder">
                      Select an album or a track to view details.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
      </DashboardSection>
    </div>
  );
};

export default BrowsePage;
